// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: feedback asks from Konvo as the person sees them (FeedbackCard): one loaded view per ask
// id, shared by every place the card is drawn (the Konvo DM, the Rail), and the answers sent from
// it. Kept for the session so a card scrolled out and back keeps what was sent.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import type {FeedbackAnswer, FeedbackAnswerInput, FeedbackAskView} from '@fluxer/schema/src/domains/konvo/AppFeedback';
import {makeAutoObservable, runInAction} from 'mobx';

export type AskState = {status: 'loading'} | {status: 'ready'; view: FeedbackAskView} | {status: 'missing'};

class FeedbackAsksStore {
	private asks = new Map<string, AskState>();
	sending = new Set<string>();

	constructor() {
		makeAutoObservable(this);
	}

	get(askId: string): AskState | null {
		return this.asks.get(askId) ?? null;
	}

	/** Loads the ask once per session (or again with `force`). */
	load(askId: string, force = false): void {
		const current = this.asks.get(askId);
		if (current && !force && current.status !== 'missing') return;
		if (!current) this.asks.set(askId, {status: 'loading'});
		void http.get<FeedbackAskView>(Endpoints.KONVO_FEEDBACK_ASK(askId), RETRY_RATE_LIMITS).then(
			(response) =>
				runInAction(() => {
					this.asks.set(askId, response.body ? {status: 'ready', view: response.body} : {status: 'missing'});
				}),
			() =>
				runInAction(() => {
					if (this.asks.get(askId)?.status !== 'ready') this.asks.set(askId, {status: 'missing'});
				}),
		);
	}

	isSending(askId: string): boolean {
		return this.sending.has(askId);
	}

	/** Sends one answer; resolves with it, or rejects (the card says it could not send). */
	async answer(askId: string, input: FeedbackAnswerInput): Promise<FeedbackAnswer> {
		this.sending.add(askId);
		try {
			const response = await http.post<FeedbackAnswer>(Endpoints.KONVO_FEEDBACK_ASK_ANSWERS(askId), {body: input});
			const answer = response.body;
			runInAction(() => {
				const state = this.asks.get(askId);
				if (state?.status === 'ready') {
					this.asks.set(askId, {status: 'ready', view: {...state.view, answers: [...state.view.answers, answer]}});
				}
			});
			return answer;
		} finally {
			runInAction(() => {
				this.sending.delete(askId);
			});
		}
	}
}

export default new FeedbackAsksStore();
